import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { MenuItem } from 'primeng/api';
import { NavItem } from '../navigation/nav-item.model';

/**
 * The signed-in user at the foot of the sidebar, opening a menu with the account entries (help,
 * settings), the language choice and log out.
 */
@Component({
    selector: 'app-account-menu',
    templateUrl: './account-menu.component.html',
    styleUrls: ['./account-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AccountMenuComponent implements OnChanges {
    @Input() name = '';
    @Input() email = '';
    @Input() entries: NavItem[] = [];
    @Input() languages: string[] = [];
    @Input() currentLanguage = '';
    /** An account entry was picked: lets the compact layout fold the sidebar away. */
    @Output() readonly selected = new EventEmitter<void>();
    @Output() readonly languageChange = new EventEmitter<string>();
    @Output() readonly logout = new EventEmitter<void>();

    items: MenuItem[] = [];
    /** The language switch: a single row, drawn by the template as one button per language. */
    languageItem: MenuItem = {};

    constructor(private readonly translateService: CustomTranslateService) {}

    /** The user's name, or their e-mail address when the account has no name. */
    get displayName(): string {
        return this.name || this.email;
    }

    get initials(): string {
        const words = this.displayName.split(/[\s@.]+/).filter(Boolean);
        return words
            .slice(0, 2)
            .map((word) => word[0].toUpperCase())
            .join('');
    }

    ngOnChanges(): void {
        // A flat list split by separators: p-menu would turn every top-level entry into a group
        // header once one has children, and the account entries have no header of their own.
        // Icons are Material Symbols names, drawn by the template's item slot.
        this.languageItem = { label: this.translateService.instant('menu.shell.language'), icon: 'translate' };
        const groups: MenuItem[][] = [
            this.entries.map((entry) => this.toMenuItem(entry)),
            this.languages.length > 0 ? [this.languageItem] : [],
            [
                {
                    label: this.translateService.instant('menu.actions.logout'),
                    icon: 'logout',
                    command: () => this.logout.emit(),
                },
            ],
        ].filter((group) => group.length > 0);
        this.items = groups.flatMap((group, i) => (i === 0 ? group : [{ separator: true }, ...group]));
    }

    private toMenuItem(entry: NavItem): MenuItem {
        return {
            label: entry.label,
            icon: entry.icon,
            // p-menu skips the command of a disabled entry, but its link would still navigate.
            routerLink: entry.disabled ? undefined : entry.route,
            disabled: entry.disabled,
            automationId: entry.testId,
            tooltipOptions: entry.disabledHint ? { tooltipLabel: entry.disabledHint } : undefined,
            command: () => {
                entry.action?.();
                this.selected.emit();
            },
        };
    }
}
